import React from 'react';
import { TDR } from 'tdr-common';
import { SearchFilters } from '../../routes/AvailabilityGuard';
import { CustomMessageSection } from './sections/CustomMessageSection';
import { EventsSection } from './sections/EventsSection';
import { InboundDirectSection } from './sections/InboundDirectSection';
import { InboundLinkSection } from './sections/InboundLinkSection';
import { InboundTablzRestaurantSection } from './sections/InboundTablzRestaurantSection';
import { getSearchResult } from '../inventory-carousel/categorizeTables';
import { InventorySection, InventorySectionProps } from './sections/InventorySection';
import { resolveInventorySection } from './sections/inventoryTables';
import { StandardSeatingSection } from './sections/StandardSeatingSection';

export type ResultsSectionsProps = {
	sections: TDR.SearchConfig.Section[];
	restaurant: TDR.Restaurant;
	searchFilters: SearchFilters;
	inventory: Omit<InventorySectionProps, 'section' | 'restaurant' | 'searchFilters' | 'positionOffset'>;
};

/**
 * Analytics reports a card's `position` within the whole results page, but each inventory
 * section counts its own cards from zero. A config that splits inventory across several
 * sections would otherwise emit position 0 more than once, so give each section the running
 * total of the cards rendered above it.
 */
function inventoryPositionOffsets(
	sections: TDR.SearchConfig.Section[],
	inventory: ResultsSectionsProps['inventory'],
	searchFilters: SearchFilters
): number[] {
	const offsets: number[] = [];
	let total = 0;
	sections.forEach((section, index) => {
		if (section.type !== 'inventory') {
			return;
		}
		offsets[index] = total;
		total += resolveInventorySection(section, {
			categories: inventory.categories,
			searchFilters,
			emptyState: inventory.emptyState,
			timeslotDocMap: inventory.timeslotDocMap,
			availableTimesFor: (tableId) => getSearchResult(inventory.results?.resultMap, tableId)?.availableTimes
		}).cardCount;
	});
	return offsets;
}

export function ResultsSections({ sections, restaurant, searchFilters, inventory }: ResultsSectionsProps) {
	const positionOffsets = inventoryPositionOffsets(sections, inventory, searchFilters);

	return (
		<>
			{sections.map((section, index) => {
				const key = `${section.type}-${index}`;
				switch (section.type) {
					case 'standard-seating':
						return <StandardSeatingSection key={key} section={section} restaurant={restaurant} showSkeleton={inventory.showSkeleton} />;
					case 'inventory':
						return <InventorySection key={key} section={section} restaurant={restaurant} searchFilters={searchFilters} {...inventory} positionOffset={positionOffsets[index]} />;
					case 'custom-message':
						return <CustomMessageSection key={key} section={section} />;
					case 'inbound-tablz-restaurant':
						return <InboundTablzRestaurantSection key={key} section={section} restaurant={restaurant} />;
					case 'inbound-direct':
						return <InboundDirectSection key={key} restaurant={restaurant} section={section} />;
					case 'inbound-link':
						return <InboundLinkSection key={key} section={section} />;
					case 'events':
						return <EventsSection key={key} restaurant={restaurant} />;
					default:
						return null;
				}
			})}
		</>
	);
}
